import type { HTMLAttributes, KeyboardEvent, ReactNode } from 'react';
import { cx } from './cx.js';
import { QuietGround } from './QuietGround.js';
import { useFocusTrap } from './useFocusTrap.js';

/**
 * The room over everything: a fullscreen sheet the game waits behind.
 *
 * Six screens wrote this out by hand — pass-and-play's handoff, both game-over
 * sheets, the leave prompt, the delete-account prompt, and the review panel.
 * All six say `role="dialog" aria-modal="true"`. **Five of the six never took
 * focus.** `aria-modal` tells assistive technology that everything outside the
 * dialog is inert, so five of them announced the page unavailable and then left
 * the keyboard standing on it. That is the bug this component exists for; the
 * paint is the smaller half.
 *
 * Three of the six also had no reduced-motion escape, and the cause was not
 * carelessness. The escape was written as `data-[motion=none]:…`, which needs a
 * `data-motion` attribute, which needs a `reducedMotion` value threaded down
 * from the screen — and the three covers that went without are exactly the
 * three with no such value in scope. A fix that still needed the prop would
 * have been forgotten the same way, so the escape is doubled here:
 *
 * - `motion-reduce:` is the operating system's own setting. It is a media
 *   query, it needs nothing threaded anywhere, and a screen cannot forget it.
 *   This is the half that closed the gap.
 * - `data-motion` is the in-app setting, which is *wider*: it also covers
 *   fast-forward, where a player is skipping to the end and nothing should
 *   animate whatever the device thinks.
 *
 * A cover is not a `Scrim`. A scrim quiets the felt and leaves it legible,
 * because what is behind it is what the panel in front is about. A cover paints
 * `bg-ground` over the whole screen, because what is behind it is either
 * private — the last player's hand — or finished.
 */

export type CoverGround =
  /** Bare porcelain. What a handoff and a finished game get. */
  | 'plain'
  /** The ground motif, two paper suits, behind the words. */
  | 'suits';

export type CoverLayout =
  /** A short message, read in the middle of the screen. Five of the six. */
  | 'center'
  /** A list that can outgrow the screen, read from the top. The review panel. */
  | 'scroll';

export interface CoverProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  /*
   * `role`, `aria-modal` and `tabIndex` are the contract, not defaults. They
   * were set above the `{...rest}` spread, which left a caller free to
   * override the two properties this component was promoted to guarantee —
   * and five of the six covers it replaced had already got exactly this
   * wrong by hand. Out of the props type, so it cannot be done at all.
   */
  'className' | 'children' | 'role' | 'aria-modal' | 'tabIndex'
> {
  readonly layout?: CoverLayout;
  /**
   * What the cover is painted on. `plain` is the default and stays the default:
   * a handoff and a game that is over are both moments to get through, and a
   * decorated field slows them down. `suits` is for the one question a player
   * is asked to answer against the match itself — forfeiting it — where the
   * blank ground said nothing about what was being given up.
   */
  readonly ground?: CoverGround;
  /**
   * The in-app motion setting, where the screen has one. Optional, and that is
   * the whole design: `motion-reduce:` below holds the line without it, so a
   * cover that cannot reach the setting is still correct rather than silently
   * animating. Pass it where you have it — fast-forward is not a device
   * preference and only this prop can see it.
   */
  readonly reducedMotion?: boolean | undefined;
  /**
   * What Escape does, for a cover that has a way out — the review panel closes,
   * the leave prompt stays, the delete confirm cancels. Omit it for one that has
   * none: a handoff has to be acknowledged and a finished game has nowhere to go
   * back to, so those three get no dismissal invented for them.
   */
  readonly onEscape?: (() => void) | undefined;
  /** Layout and spacing only. The surface is not the caller's to change. */
  readonly className?: string | undefined;
  readonly children: ReactNode;
}

const COVER =
  'cover fixed inset-0 z-50 flex flex-col items-center text-center bg-ground focus:outline-none motion-off ';

/*
 * Layout is a variant rather than something `className` overrides, because
 * `justify-center` and `justify-start` are the same utility family: which one
 * wins is decided by the order Tailwind emits them in the stylesheet, not by
 * the order they appear in the attribute. A caller passing `justify-start`
 * would look right and do nothing. `gap-4` against `gap-6` is the same trap.
 *
 * `scroll` is the only layout whose content reaches the top and bottom edges,
 * so it is the only one that has to clear a notch.
 */
const LAYOUT = {
  center: 'justify-center gap-6 p-8',
  scroll: 'justify-start gap-4 overflow-y-auto px-8 cover-scroll',
} satisfies Record<CoverLayout, string>;

export function Cover({
  layout = 'center',
  ground = 'plain',
  reducedMotion,
  onEscape,
  className,
  children,
  onKeyDown,
  ...rest
}: CoverProps): React.JSX.Element {
  const trap = useFocusTrap<HTMLDivElement>(onEscape);

  /*
   * The trap runs first and the caller's handler second, so a screen can still
   * listen for its own keys without having to re-implement Tab to get them.
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    trap.onKeyDown(event);
    onKeyDown?.(event);
  };

  return (
    <div
      className={cx(COVER, LAYOUT[layout], className)}
      ref={trap.ref}
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
      data-motion={reducedMotion === undefined ? undefined : reducedMotion ? 'none' : 'full'}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {/* Behind the words, and behind them without the caller having to know:
          the cover is `fixed` at `z-50`, so it is its own stacking context and
          a negative index puts the ground above its own `bg-ground` and under
          every child. The alternative was wrapping the children in a
          `relative` box, which would have collapsed `gap-6` between them
          into one item and made this variant change the layout of the others.

          `overflow-hidden` stays on the ground, where it belongs: on the
          cover it would stop the `scroll` layout from scrolling. */}
      {ground === 'suits' && <QuietGround className="-z-10" />}
      {children}
    </div>
  );
}
